/**
 * Déballage de l'enveloppe des fonctions Azure d'écriture.
 *
 * Les handlers `perimetres`, `entrees`, `thesaurus` (et `notes`) répondent à un
 * POST/PUT par `{ success, data: <entité>, message, timestamp }` — jamais par
 * l'entité nue. Un client typé `Promise<Perimetre>` qui rend `response.data`
 * tel quel livre donc un objet SANS `_id` : l'appelant croit avoir créé
 * l'entité, puis sa mise à jour repart en création (409 sur l'index unique
 * `{userId, name}` — cas vu sur l'import Obsidian, lineage des périmètres).
 * Même famille que la PR #201 pour les GET.
 */
export interface ApiEnvelope<T> {
    success?: boolean;
    data?: T;
    message?: string;
}

/**
 * Rend l'entité portée par une réponse d'écriture, qu'elle soit enveloppée
 * (`{ data }`) ou nue, et refuse franchement une réponse sans `_id` plutôt
 * que de laisser fuir un objet inutilisable.
 */
export function unwrapSavedEntity<T extends { _id?: string }>(payload: ApiEnvelope<T> | T | undefined, what: string): T {
    const candidate = payload && typeof payload === 'object' && 'data' in payload && payload.data && typeof payload.data === 'object'
        ? (payload as ApiEnvelope<T>).data
        : (payload as T | undefined);
    if (!candidate || typeof candidate !== 'object' || !candidate._id) {
        throw new Error(`Réponse inattendue à la sauvegarde ${what} : entité sans _id`);
    }
    return candidate;
}

/**
 * Refus motivé du serveur : une réponse d'erreur qui porte un `code` et un
 * `message` (ex. 409 `NOM_RESERVE`, `DOSSIER_GENERAL`, `NOM_DEJA_PRIS` de
 * `/api/perimetres`). Son message est écrit pour l'écran ; une erreur sans
 * `code` (404, 500, réseau) ne l'est pas et garde son repli générique.
 */
export class ServerRefusalError extends Error {
    readonly code: string;
    constructor(code: string, message: string) {
        super(message);
        this.name = 'ServerRefusalError';
        this.code = code;
    }
}

/** Le refus motivé porté par une erreur axios, ou `null`. */
export function serverRefusalFrom(error: unknown): ServerRefusalError | null {
    const data = (error as { response?: { data?: unknown } } | null)?.response?.data as
        { code?: unknown; message?: unknown } | undefined;
    if (data && typeof data.code === 'string' && typeof data.message === 'string' && data.message.trim()) {
        return new ServerRefusalError(data.code, data.message);
    }
    return null;
}

/**
 * Message à afficher d'un refus motivé — que l'erreur soit la `ServerRefusalError`
 * elle-même ou sa forme sérialisée par Redux Toolkit (`unwrap()` d'un thunk rejeté,
 * qui garde `name`, `message` et `code`) —, sinon `null`.
 */
export function serverRefusalMessage(error: unknown): string | null {
    const e = error as { name?: unknown; message?: unknown } | null;
    if (e && e.name === 'ServerRefusalError' && typeof e.message === 'string' && e.message) return e.message;
    return null;
}
